import { Vector3 } from "three";
import { GeometryUtils, isDefined } from "../../infra3d";
import { FeatureStyle, Geometry, GEOMETRYTYPE, LineStringGeometry, MultiPointGeometry, PointGeometry, PolygonGeometry, MultiPolygonGeometry, MultiLineStringGeometry, } from "./internal";
/**
 * Representation of the FeatureCollection-Object specified in GeoJSON-Standard.
 *
 * @class Feature
 */
export class Feature {
    constructor(parentcollection) {
        /**
         * The GeoJSON type. Should always be "Feature"
         * @property {String} type
         */
        this.type = "Feature";
        /**
         * The unique feature id.
         * @property {Number} id
         */
        this.id = null;
        /**
         * Properties of this feature.
         * @property {Object} properties
         */
        this.properties = {};
        /**
         * An array of addtional geometries only for rendering (e.g. A Polygon needs also a linestring and point geometries.
         * @property {Array<Geometry>} rendergeometries
         */
        this.rendergeometries = [];
        /**
         * The highlight state of this Feature.
         * @property {Boolean} highlighton
         */
        this.highlighton = false;
        /**
         * The select state of this Feature.
         * @property {Boolean} isselected
         */
        this.isselected = false;
        /**
         * The belonging feature collection
         * @property {FeatureCollection} parentcollection
         */
        this.parentcollection = null;
        this.geometry = null;
        this.selpointindicator = null;
        this.ishidden = false;
        this.parentcollection = parentcollection;
        this.scene = parentcollection.scene;
        this.style = parentcollection.style;
        this.highlightstyle = parentcollection.highlightstyle;
        this.selpointindstyle = new FeatureStyle({
            stylename: "selectpointstyle",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        size: 8,
                        stroke: [0.051, 0.573, 0.824, 1], // inovitas blue primary
                    },
                },
            ],
        });
    }
    /**
     * Creates an empty Geometry for the Feature-Object.
     * @method createGeometry
     * @param {number} type The Geometry-Type. Currently supported are POINT, LINESTRING, MULTILINESTRING, MULTIPOINT, POLYGON and MULTIPOLYGON
     * @return {Geometry} Returns a Geometry-Object if the creation is successful, otherwise it returns null.
     */
    createGeometry(type) {
        let geometry = null;
        switch (type) {
            case GEOMETRYTYPE.POINT:
                geometry = new PointGeometry(this);
                break;
            case GEOMETRYTYPE.LINESTRING:
                geometry = new LineStringGeometry(this);
                break;
            case GEOMETRYTYPE.MULTILINESTRING:
                geometry = new MultiLineStringGeometry(this);
                break;
            case GEOMETRYTYPE.POLYGON:
                geometry = new PolygonGeometry(this);
                break;
            case GEOMETRYTYPE.MULTIPOINT:
                geometry = new MultiPointGeometry(this);
                break;
            case GEOMETRYTYPE.MULTIPOLYGON:
                geometry = new MultiPolygonGeometry(this);
                break;
            default:
                break;
        }
        return geometry;
    }
    /**
     * Create a Feature Object with content parse from GeoJson Feature node.
     * @method parseFromJSON
     * @param {Object} f a GeoJSON Feature Node.
     */
    parseFromJSON(f) {
        if (f["type"] == "Feature") {
            this.type = f["type"];
            if (isDefined(f["properties"]))
                this.properties = f["properties"];
            if (isDefined(f["id"]))
                this.id = f["id"];
            if (isDefined(f["geometry"]) && f["geometry"] != null) {
                const g = f["geometry"];
                if (typeof g["type"] == "number")
                    g["type"] = Geometry.toString(g["type"]);
                const geometry = this.createGeometry(Geometry.toNumber(g["type"]));
                if (geometry !== null) {
                    geometry.parseFromJSON(g);
                    this.geometry = geometry;
                    //add additional render geometries
                    switch (Geometry.toNumber(g["type"])) {
                        case GEOMETRYTYPE.MULTIPOINT: {
                            //create a select point geometry
                            this.createSelPoint(g);
                            break;
                        }
                        case GEOMETRYTYPE.LINESTRING: {
                            // add points to every vertice
                            const geom_mp = this.createGeometry(GEOMETRYTYPE.MULTIPOINT);
                            const coords_mp = g["coordinates"].slice(0);
                            const json_mp = {
                                type: Geometry.toNumber("MultiPoint"),
                                coordinates: coords_mp,
                            };
                            geom_mp.parseFromJSON(json_mp);
                            this.rendergeometries.push(geom_mp);
                            //create a select point geometry
                            const geom_p = this.createGeometry(GEOMETRYTYPE.POINT);
                            const json_p = {
                                type: Geometry.toNumber("Point"),
                                coordinates: g["coordinates"].slice(0)[0],
                            };
                            geom_p.parseFromJSON(json_p);
                            this.selpointindicator = geom_p;
                            this.selpointindicator.node.object3D.renderOrder = 1; // Render the selection always in front of the main geometry
                            this.selpointindicator.isSelectionIndicatorPoint = true;
                            this.selpointindicator.node.setStyle(this.selpointindstyle.toMaterialDefinitions(null));
                            this.selpointindicator.hide();
                            break;
                        }
                        case GEOMETRYTYPE.MULTILINESTRING: {
                            // add points to every vertice
                            const geom_mp = this.createGeometry(GEOMETRYTYPE.MULTIPOINT);
                            let coords_mp = [];
                            for (const coords of g["coordinates"]) {
                                const coords_mp_part = coords.slice(0);
                                coords_mp_part.pop();
                                coords_mp = coords_mp.concat(coords_mp_part);
                            }
                            const json_mp = {
                                type: Geometry.toNumber("MultiPoint"),
                                coordinates: coords_mp,
                            };
                            geom_mp.parseFromJSON(json_mp);
                            this.rendergeometries.push(geom_mp);
                            break;
                        }
                        case GEOMETRYTYPE.POLYGON: {
                            //add a surrounding linestring
                            const geom_l = this.createGeometry(GEOMETRYTYPE.LINESTRING);
                            const coords_1 = g["coordinates"][0].slice(0);
                            const json_1 = {
                                type: Geometry.toNumber("LineString"),
                                coordinates: coords_1,
                            };
                            geom_l.parseFromJSON(json_1);
                            this.rendergeometries.push(geom_l);
                            // add points to every vertices
                            const geom_mp = this.createGeometry(GEOMETRYTYPE.MULTIPOINT);
                            const coords_mp = g["coordinates"][0].slice(0);
                            coords_mp.pop();
                            const json_mp = {
                                type: Geometry.toNumber("MultiPoint"),
                                coordinates: coords_mp,
                            };
                            geom_mp.parseFromJSON(json_mp);
                            this.rendergeometries.push(geom_mp);
                            //create a select point geometry
                            const geom_p = this.createGeometry(GEOMETRYTYPE.POINT);
                            const json_p = {
                                type: Geometry.toNumber("Point"),
                                coordinates: g["coordinates"].slice(0)[0][0],
                            };
                            geom_p.parseFromJSON(json_p);
                            this.selpointindicator = geom_p;
                            this.selpointindicator.node.object3D.renderOrder = 1; // Render the selection always in front of the main geometry
                            this.selpointindicator.isSelectionIndicatorPoint = true;
                            this.selpointindicator.node.setStyle(this.selpointindstyle.toMaterialDefinitions(null));
                            this.selpointindicator.hide();
                            break;
                        }
                        case GEOMETRYTYPE.MULTIPOLYGON: {
                            //add a surrounding linestring
                            const geom_l = this.createGeometry(GEOMETRYTYPE.MULTILINESTRING);
                            const coords_1 = [];
                            for (const coords_poly of g["coordinates"]) {
                                coords_1.push(coords_poly[0].slice(0));
                            }
                            const json_1 = {
                                type: Geometry.toNumber("MultiLineString"),
                                coordinates: coords_1,
                            };
                            geom_l.parseFromJSON(json_1);
                            this.rendergeometries.push(geom_l);
                            // add points to every vertices
                            const geom_mp = this.createGeometry(GEOMETRYTYPE.MULTIPOINT);
                            let coords_mp = [];
                            for (const coords of g["coordinates"]) {
                                const coords_mp_part = coords[0].slice(0);
                                coords_mp_part.pop();
                                coords_mp = coords_mp.concat(coords_mp_part);
                            }
                            const json_mp = {
                                type: Geometry.toNumber("MultiPoint"),
                                coordinates: coords_mp,
                            };
                            geom_mp.parseFromJSON(json_mp);
                            this.rendergeometries.push(geom_mp);
                            break;
                        }
                    }
                }
            }
        }
    }
    /**
     * returns the Feature content as GeoJson string
     * @method toGeoJsonString
     * @return {string}
     */
    toGeoJsonString() {
        const geoJSON = this.toGeoJSON();
        return JSON.stringify(geoJSON);
    }
    /**
     * returns the Feature content as GeoJson
     * @method toGeoJSON
     * @return {Object}
     */
    toGeoJSON() {
        const jsonobject = {};
        jsonobject["type"] = "Feature";
        if (this.geometry != null) {
            jsonobject["geometry"] = JSON.parse(this.geometry.toGeoJsonString());
        }
        else {
            jsonobject["geometry"] = null;
        }
        jsonobject["properties"] = this.properties;
        jsonobject["id"] = this.id;
        return jsonobject;
    }
    /**
     *  Destroys the feature
     *  @method destroy
     *  @param {boolean?} needsrender
     */
    destroy(needsrender = false) {
        if (this.geometry != null) {
            this.geometry.destroy();
            this.geometry = null;
        }
        for (let i = 0; i < this.rendergeometries.length; i++) {
            this.rendergeometries[i].destroy();
        }
        this.rendergeometries = [];
        this.highlighton = false;
        this.properties = null;
        if (this.selpointindicator != null)
            this.selpointindicator.destroy();
        if (needsrender)
            this.parentcollection.infrascene.render();
    }
    /**
     * redraws the geoemtry
     * @method update
     * @param {boolean} needsrender
     */
    update(needsrender = false) {
        if (this.geometry != null) {
            this.geometry.update();
            if (needsrender)
                this.parentcollection.infrascene.render();
            for (let i = 0; i < this.rendergeometries.length; i++) {
                this.rendergeometries[i].update();
            }
        }
    }
    /**
     * Mark the feature as selected or not
     */
    selected(value) {
        this.isselected = value;
    }
    /**
     * Mark the feature as selected
     */
    select() {
        this.selected(true);
    }
    /**
     * Mark the feature as not selected
     */
    deselect() {
        this.selected(false);
    }
    /**
     * Shows the geoemtry
     */
    show() {
        if (this.geometry != null) {
            for (let i = 0; i < this.rendergeometries.length; i++) {
                this.rendergeometries[i].show();
            }
            this.geometry.show();
            this.ishidden = false;
        }
    }
    /**
     * Hides the geometry
     */
    hide() {
        if (this.geometry != null) {
            for (let i = 0; i < this.rendergeometries.length; i++) {
                this.rendergeometries[i].hide();
            }
            this.geometry.hide();
            this.ishidden = true;
            if (this.selpointindicator != null)
                this.selpointindicator.hide();
        }
    }
    /**
     * highlights the geometry
     * @method highlightOn
     * @param {boolean?} needsrender
     */
    highlightOn(needsrender = false) {
        if (this.geometry != null && !this.highlighton) {
            this.geometry.highlightOn();
            for (let i = 0; i < this.rendergeometries.length; i++) {
                this.rendergeometries[i].highlightOn();
            }
            if (needsrender)
                this.parentcollection.infrascene.render();
            this.highlighton = true;
        }
    }
    /**
     * unhighligt the geometry
     * @method highlightOff
     * @param {boolean?} needsrender
     */
    highlightOff(needsrender = false) {
        if (this.geometry != null && this.highlighton) {
            this.geometry.highlightOff();
            for (let i = 0; i < this.rendergeometries.length; i++) {
                this.rendergeometries[i].highlightOff();
            }
            if (this.selpointindicator != null)
                this.selpointindicator.hide();
            if (needsrender)
                this.parentcollection.infrascene.render();
            this.highlighton = false;
        }
    }
    /**
     * highlights a point of the geometry
     * @method highlightPointOn
     */
    highlightPointOn(index) {
        if (this.geometry != null && this.selpointindicator != null) {
            this.selpointindicator.movePoint(this.geometry.coordinates[index]);
            this.selpointindicator.show();
        }
    }
    /**
     * unhighlights the highlighted point of the geometry
     * @method highlightPointOff
     */
    highlightPointOff() {
        if (this.selpointindicator)
            this.selpointindicator.hide();
    }
    /**
     * create a select point geometry
     * @param g
     */
    createSelPoint(g) {
        if (this.selpointindicator == null) {
            const geom = this.createGeometry(GEOMETRYTYPE.POINT);
            const jsonp = {
                type: Geometry.toNumber("Point"),
                coordinates: g["coordinates"].slice(0)[0],
            };
            geom.parseFromJSON(jsonp);
            this.selpointindicator = geom;
            this.selpointindicator.node.object3D.renderOrder = 1; // Render the selection always in front of the main geometry
            this.selpointindicator.isSelectionIndicatorPoint = true;
            this.selpointindicator.node.setStyle(this.selpointindstyle.toMaterialDefinitions(null));
            this.selpointindicator.hide();
        }
    }
    /**
     * Moves a point in the geometry of this feature and also updates the tempgeometries for drawing
     * @method movePoint
     * @param {number[]} newcoord the new coordinate
     * @param {?number=} index the index in this features arraylist if the feature is a POINT the index is unnesscesary
     */
    movePoint(newcoord, index) {
        if (this.geometry != null) {
            this.geometry.movePoint(newcoord, index);
            for (let i = 0; i < this.rendergeometries.length; i++) {
                this.rendergeometries[i].movePoint(newcoord, index);
            }
            if (this.selpointindicator != null)
                this.selpointindicator.movePoint(newcoord);
        }
    }
    /**
     * Deletes a Point in the feature geometry
     * @method deletePoint
     * @param {number} index the poitnindex in feature.geometry array
     */
    deletePoint(index) {
        switch (this.geometry.type) {
            case GEOMETRYTYPE.POINT:
                alert("Can't delete this point at least 1 point is needed");
                break; //point deletion not possible....
            case GEOMETRYTYPE.MULTIPOINT:
                this.geometry.deletePoint(index);
                for (let i = 0; i < this.rendergeometries.length; i++) {
                    this.rendergeometries[i].deletePoint(index);
                }
                break;
            case GEOMETRYTYPE.LINESTRING:
                if (this.geometry.coordinates.length > 2) {
                    this.geometry.deletePoint(index);
                    for (let i = 0; i < this.rendergeometries.length; i++) {
                        this.rendergeometries[i].deletePoint(index);
                    }
                }
                break;
            case GEOMETRYTYPE.POLYGON:
                if (this.geometry.coordinates.length > 3) {
                    this.geometry.deletePoint(index);
                    for (let i = 0; i < this.rendergeometries.length; i++) {
                        this.rendergeometries[i].deletePoint(index);
                        if (index == 0 && this.rendergeometries[i].type == GEOMETRYTYPE.LINESTRING) {
                            const newcoord = this.rendergeometries[i].coordinates[0];
                            this.rendergeometries[i].movePoint(newcoord, this.rendergeometries[i].coordinates.length - 1);
                        }
                    }
                }
                break;
        }
        if (this.selpointindicator != null) {
            this.selpointindicator.hide();
            if (index < this.geometry.coordinates.length) {
                this.highlightPointOn(index);
            }
            else {
                this.highlightPointOn(index - 1);
            }
        }
    }
    /**
     * inserts a new point
     * @method insertPoint
     * @param {number} index
     */
    insertPoint(index) {
        switch (this.geometry.type) {
            case GEOMETRYTYPE.POINT:
                break; //point insertion not possible....
            case GEOMETRYTYPE.MULTIPOINT: {
                const nextpoint = this.geometry.coordinates[index];
                this.geometry.insertPoint(nextpoint, index);
                for (let i = 0; i < this.rendergeometries.length; i++) {
                    this.rendergeometries[i].insertPoint(nextpoint, index);
                }
                break;
            }
            case GEOMETRYTYPE.LINESTRING: {
                const nextpoint = this.geometry.coordinates[index];
                this.geometry.insertPoint(nextpoint, index);
                for (let i = 0; i < this.rendergeometries.length; i++) {
                    this.rendergeometries[i].insertPoint(nextpoint, index);
                }
                break;
            }
            case GEOMETRYTYPE.POLYGON: {
                const nextpoint = this.geometry.coordinates[index].slice(0);
                nextpoint[1] = nextpoint[1] + 0.01; //change height 30cm to prevent double points in triangulation...
                this.geometry.insertPoint(nextpoint, index);
                for (let i = 0; i < this.rendergeometries.length; i++) {
                    this.rendergeometries[i].insertPoint(nextpoint, index);
                }
                break;
            }
        }
    }
    pick(mx, my, pickdataarray) {
        if (this.geometry != null && !this.ishidden) {
            this.geometry.pick(mx, my, pickdataarray);
            for (let i = 0; i < this.rendergeometries.length; i++) {
                this.rendergeometries[i].pick(mx, my, pickdataarray);
            }
        }
    }
    changeStyle(style, highlightstyle) {
        this.style = style;
        this.highlightstyle = highlightstyle;
        this.geometry.update();
        for (let i = 0; i < this.rendergeometries.length; i++) {
            this.rendergeometries[i].update();
        }
    }
    static calcDeltaHeight(feature) {
        let deltah = 0;
        if (feature.geometry.type == "LineString") {
            const coords = feature.geometry.coordinates;
            deltah = coords[coords.length - 1][2] - coords[0][2];
        }
        return deltah;
    }
    static calcDistance(feature, horizontal) {
        let dist = 0;
        if (feature.geometry.type == "LineString") {
            const coords = feature.geometry.coordinates;
            let p1 = coords[0];
            for (let i = 1; i < coords.length; i++) {
                const p2 = coords[i];
                let partDist = Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2);
                if (!horizontal)
                    partDist += Math.pow(p1[2] - p2[2], 2);
                dist = dist + Math.sqrt(partDist);
                p1 = p2;
            }
        }
        return dist;
    }
    static calcArea(feature, area3D) {
        let area = 0;
        if (feature.geometry.type == "Polygon") {
            const coords = [];
            for (let pts = 0; pts < feature.geometry.coordinates[0].length; pts++) {
                coords.push(new Vector3(feature.geometry.coordinates[0][pts][0], feature.geometry.coordinates[0][pts][1], feature.geometry.coordinates[0][pts][2]));
            }
            if (area3D) {
                area = GeometryUtils.CalcArea(coords);
            }
            else {
                area = GeometryUtils.CalcArea2D(coords);
            }
        }
        return area;
    }
}
//# sourceMappingURL=feature.js.map